import { Link } from "@tanstack/react-router";
import { cx } from "@/utils/cx";
import { useTranslation } from "react-i18next";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { Badge } from "@/components/base/badges/badges";
import { useFeatures, visibilityOf, isNavVisible } from "@/lib/license";
import { visibleGroups, type SettingsItemState } from "./settingsNav";

/**
 * Přehled — rozcestník na `/settings`. Používá stejný config i stejný feature
 * filtr jako tab lišta ({@link visibleGroups}), takže karty a navigace nemůžou
 * rozejít. Skupina `overview` (bez položek) se vynechává.
 */
export function SettingsOverviewPage() {
  const { t } = useTranslation();
  const features = useFeatures();
  const show = (id: string) => isNavVisible(visibilityOf(features.data, id));
  const stateOf = (id: string): SettingsItemState => {
    const v = visibilityOf(features.data, id);
    return v === "LOCKED" ? "locked" : v === "ADVERTISED" ? "advertised" : "enabled";
  };
  const groups = visibleGroups(show, stateOf).filter((g) => g.items.length > 0);

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <section key={group.id} className="flex flex-col gap-3">
          <h2 className="text-xs font-semibold tracking-wider text-quaternary uppercase">{t(group.labelKey)}</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.items.map(({ to, labelKey, descKey, icon: Icon, state }) => {
              const blocked = state && state !== "enabled";
              return (
                <Link
                  key={to}
                  to={to}
                  className={cx(
                    "flex items-start gap-3 rounded-xl bg-primary p-4 ring-1 ring-secondary transition hover:ring-brand",
                    blocked && "opacity-75",
                  )}
                >
                  <FeaturedIcon icon={Icon} theme="light" color={blocked ? "gray" : "brand"} size="md" />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-semibold text-primary">{t(labelKey)}</span>
                      {blocked && (
                        <Badge size="sm" color={state === "locked" ? "warning" : "brand"}>
                          {t(`feature.${state}.badge`)}
                        </Badge>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-tertiary">{t(descKey)}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
